"use client"

import { useEffect, useState } from "react"
import { Box, Button, Typography, useTheme } from "@mui/material"
import { Female, Male } from "@mui/icons-material"
import { useTranslation } from 'react-i18next'
import { I18nNamespace } from '@/i18n/types/i18nNamespace'
import type { I18nPersonasNs } from '@/i18n/dictionaries/interfaces'

interface GenderSelectorProps {
  value?: string
  onChange?: (value: string) => void
  sx?: object
  disabled?: boolean
}

export default function GenderSelector({ value, onChange, sx, disabled = false }: GenderSelectorProps) {
  const [selectedGender, setSelectedGender] = useState(value || "")
  const theme = useTheme()
  const { t: translate } = useTranslation(I18nNamespace.Personas)

  useEffect(() => {
    setSelectedGender(value || "")
  }, [value])

  const handleGenderSelect = (gender: string) => {
    if (disabled) {
      return
    }
    setSelectedGender(gender)
    onChange?.(gender)
  }

  const genderOptions = [
    {
      id: "male",
      label: translate(nameof.full<I18nPersonasNs>((n) => n.male)),
      icon: Male,
      description: translate(nameof.full<I18nPersonasNs>((n) => n.male)),
    },
    {
      id: "female",
      label: translate(nameof.full<I18nPersonasNs>((n) => n.female)),
      icon: Female,
      description: translate(nameof.full<I18nPersonasNs>((n) => n.female)),
    },
  ]

  return (
    <Box sx={{ width: "100%", ...sx }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
        }}
      >
        {genderOptions.map((option) => {
          const Icon = option.icon
          const isSelected = selectedGender === option.id

          return (
            <Button
              key={option.id}
              disabled={disabled}
              onClick={() => handleGenderSelect(option.id)}
              variant="text"
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                px: 2.5,
                py: 1.5,
                borderRadius: 2,
                width: 120,
                minHeight: 48,
                border: isSelected ? "2px solid #333" : "2px solid",
                borderColor: isSelected ? "#333" : "grey.400",
                backgroundColor: isSelected ? "#333" : "transparent",
                color: isSelected ? "#fff" : "text.primary",
                textTransform: "none",
                fontWeight: 500,
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  backgroundColor: isSelected ? "#555" : "rgba(0, 0, 0, 0.04)",
                  borderColor: isSelected ? "#555" : "grey.600",
                },
                "&:focus": {
                  outline: isSelected ? "none" : `2px solid ${theme.palette.primary.main}`,
                  outlineOffset: isSelected ? 0 : 2,
                  boxShadow: isSelected ? `0 0 0 2px ${theme.palette.primary.main}` : "none",
                },
              }}
              aria-label={`Select ${option.description}`}
              role="radio"
              aria-checked={isSelected}
            >
              <Icon sx={{ fontSize: 20 }} />
              <Typography
                variant="body2"
                fontWeight="medium"
                sx={{
                  color: "inherit",
                }}
              >
                {option.label}
              </Typography>
            </Button>
          )
        })}
      </Box>
    </Box>
  )
}
